import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getCaptureData } from '../services/captureData';
import { supabase } from '../utils/supabaseClient';
import { CaptureLogEntry } from '../types/CaptureLog';

// Define different polling intervals (in milliseconds)
const CAPTURE_POLLING_INTERVALS = {
  HIGH_ACTIVITY: 3000,    // 3 seconds - when new captures are frequent
  NORMAL: 45000,          // 45 seconds - normal operation
  LOW_ACTIVITY: 300000,   // 5 minutes - when few new captures
  PAUSED: 0,              // Stop polling when tab is not visible
} as const;

// Hook to detect page visibility (when user switches tabs)
const usePageVisibility = () => {
  const [isVisible, setIsVisible] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
};

// Hook to determine capture activity level based on recent captures
const useCaptureActivity = (captures: CaptureLogEntry[] | undefined) => {
  const [activityLevel, setActivityLevel] = useState<'high' | 'normal' | 'low'>('normal');

  useEffect(() => {
    if (!captures || captures.length < 2) {
      setActivityLevel('normal');
      return;
    }

    // Check how many captures were created in the last 10 minutes
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
    const recentCaptures = captures.filter(capture => new Date(capture.timestamp) > tenMinutesAgo);

    if (recentCaptures.length >= 5) {
      setActivityLevel('high');    // 5+ captures in 10 minutes = high activity
    } else if (recentCaptures.length >= 2) {
      setActivityLevel('normal');  // 2-4 captures in 10 minutes = normal activity
    } else {
      setActivityLevel('low');     // Less than 2 captures = low activity
    }
  }, [captures]);

  return activityLevel;
};

// Function to get authenticated image URL using the user's own JWT
const getAuthenticatedImageUrl = async (imageUrl: string): Promise<string> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(imageUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching authenticated image:', error);
    throw error;
  }
};

// Main hook that combines capture data and media token fetching
export const useCaptureData = () => {
  const isPageVisible = usePageVisibility();
  const [currentInterval, setCurrentInterval] = useState<number>(CAPTURE_POLLING_INTERVALS.NORMAL);
  const [authenticatedImageUrl, setAuthenticatedImageUrl] = useState<string>('');

  // Fetch capture data with adaptive polling
  const captureQuery = useQuery({
    queryKey: ['captureData'],
    queryFn: getCaptureData,
    refetchInterval: currentInterval,
    enabled: isPageVisible,
    refetchOnWindowFocus: isPageVisible,
  });

  const activityLevel = useCaptureActivity(captureQuery.data);

  // Calculate the appropriate polling interval
  const getPollingInterval = (activityLevel: 'high' | 'normal' | 'low'): number => {
    if (!isPageVisible) return 0; // 0 = stop polling
    
    switch (activityLevel) {
      case 'high':
        return CAPTURE_POLLING_INTERVALS.HIGH_ACTIVITY;
      case 'normal':
        return CAPTURE_POLLING_INTERVALS.NORMAL;
      case 'low':
        return CAPTURE_POLLING_INTERVALS.LOW_ACTIVITY;
      default:
        return CAPTURE_POLLING_INTERVALS.NORMAL;
    }
  };

  // Update polling interval when activity level or page visibility changes
  useEffect(() => {
    const newInterval = getPollingInterval(activityLevel);
    if (newInterval !== currentInterval) {
      setCurrentInterval(newInterval);
    }
  }, [activityLevel, isPageVisible, currentInterval]);

  // Update authenticated image URL when capture data changes
  useEffect(() => {
    const updateAuthenticatedImage = async () => {
      if (captureQuery.data && captureQuery.data.length > 0) {
        const latestCapture = captureQuery.data[captureQuery.data.length - 1];
        try {
          if (authenticatedImageUrl && authenticatedImageUrl.startsWith('blob:')) {
            URL.revokeObjectURL(authenticatedImageUrl);
          }
          
          const authImageUrl = await getAuthenticatedImageUrl(latestCapture.image_url);
          setAuthenticatedImageUrl(authImageUrl);
        } catch (error) {
          console.error('Failed to load authenticated image:', error);
          setAuthenticatedImageUrl('');
        }
      }
    };

    updateAuthenticatedImage();
  }, [captureQuery.data]);

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (authenticatedImageUrl && authenticatedImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(authenticatedImageUrl);
      }
    };
  }, [authenticatedImageUrl]);

  // Get the latest capture for easy access
  const latestCapture = captureQuery.data && captureQuery.data.length > 0 
    ? captureQuery.data[captureQuery.data.length - 1] 
    : null;

  // Return combined state
  return {
    // Capture data
    captureData: captureQuery.data,
    captureLoading: captureQuery.isLoading,
    captureError: captureQuery.error,
    
    // Processed data
    latestCapture,
    authenticatedImageUrl,
    
    // Polling info
    activityLevel,
    isPageVisible,
    pollingInterval: currentInterval,
    
    // Helper methods
    forceRefreshCaptures: () => captureQuery.refetch(),
  };
}; 